import React from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { Controller, useForm } from 'react-hook-form';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import QueryBasedDropdown from '../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import {
  GET_ENTITY_TERMINATION_LEGAL_REVIEW,
  GET_LEGAL_TERMINATION_TYPES,
  FINALIZE_ENTITY_TERMINATION_LEGAL_REVIEW,
} from '../../../utils/statApi/EntityTerminationApi';
import { ILegalReviewApproval, ILegalTerminationType } from '../types';
import IDropdownReference from '../../../utils/types/IDropdownReference';

interface IApproveLegalReviewPanelProps {
  companyCode: string;
  closePanel: () => void;
  data: ILegalReviewApproval;
}

interface IApproveLegalReviewForm {
  legalEffectiveDate?: string;
  notificationDate?: string;
  legalTerminationType?: IDropdownReference;
}

const ApproveLegalReviewPanel: React.FC<IApproveLegalReviewPanelProps> = ({
  companyCode,
  closePanel,
  data,
}) => {
  const { handleSubmit, control, errors, setValue, watch } = useForm<IApproveLegalReviewForm>();

  const { data: typesData } = useQuery(GET_LEGAL_TERMINATION_TYPES);
  const legalTerminationTypes: ILegalTerminationType[] =
    typesData?.legalTerminationTypes?.map((t: ILegalTerminationType) => ({
      ...t,
      isSelectable: true,
    })) ?? [];

  const approvalId = data.review?.reviewApprovals?.[0]?.id ?? 0;
  const legalEffectiveDate = watch('legalEffectiveDate');
  const notificationDate = watch('notificationDate');
  const legalTerminationType = watch('legalTerminationType');

  const [finalizeLegalReview, { loading, error }] = useMutation(
    FINALIZE_ENTITY_TERMINATION_LEGAL_REVIEW,
    {
      refetchQueries: [
        {
          query: GET_ENTITY_TERMINATION_LEGAL_REVIEW,
          variables: { companyCode },
        },
      ],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSave = (formData: IApproveLegalReviewForm): void => {
    finalizeLegalReview({
      variables: {
        input: {
          reviewId: data.reviewId,
          approvalId,
          legalEffectiveDate: formData.legalEffectiveDate,
          notificationDate: formData.notificationDate ?? null,
          legalTerminationTypeId: formData.legalTerminationType?.id ?? null,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Approve Legal Review"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Approve"
          saveTitle="Approve"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSave)}
          mutationLoading={loading}
          disabled={!legalEffectiveDate || !notificationDate || !legalTerminationType}
        />
      )}
    >
      <LoadingErrorMessage
        loading={loading}
        error={error as ApolloError}
        actionName="approving the legal review"
      />
      <Controller
        control={control}
        name="legalTerminationType"
        rules={{ required: 'Legal termination type is required.' }}
        render={({ value }): React.ReactElement => (
          <QueryBasedDropdown
            label="Legal Termination Type"
            value={value}
            outputKey="legalTerminationType"
            valueKey="legalTerminationType"
            required
            errors={errors}
            dropdownOptionsData={legalTerminationTypes}
            handleChange={(val: IDropdownReference) => setValue('legalTerminationType', val)}
          />
        )}
      />
      <Controller
        control={control}
        name="legalEffectiveDate"
        rules={{ required: 'Legal effective date is required.' }}
        render={({ value }): React.ReactElement => (
          <UtcDatePicker
            label="Legal Effective Date"
            isRequired
            value={value}
            isRequiredErrorMessage={errors?.legalEffectiveDate?.message as string}
            onSelectDate={(date) => setValue('legalEffectiveDate', date)}
          />
        )}
      />
      <Controller
        control={control}
        name="notificationDate"
        rules={{ required: 'Notification date is required.' }}
        render={({ value }): React.ReactElement => (
          <UtcDatePicker
            label="Notification Date"
            isRequired
            value={value}
            isRequiredErrorMessage={errors?.notificationDate?.message as string}
            onSelectDate={(date) => setValue('notificationDate', date)}
          />
        )}
      />
    </CoherencePanel>
  );
};

export default ApproveLegalReviewPanel;
